import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLocale } from '@incpt/i18n';
import { ScreenBackdrop } from './ScreenBackdrop';
import { BottomSheet } from './BottomSheet';
import { PinFlow } from './PinFlow';
import { NotificationsScreen } from './NotificationsScreen';
import { SeedPhraseFlow } from './SeedPhraseFlow';
import { FaqScreen } from './FaqScreen';
import { SupportScreen } from './SupportScreen';
import { PromocodeScreen } from './PromocodeScreen';
import { AmlScreen } from './AmlScreen';
import { ReferralScreen } from './ReferralScreen';
import { ReauthFlow } from './ReauthFlow';
import { ChevronRight } from './icons';
import { useTelegramUser, telegramName } from '../../hooks/useTelegramUser';
import { useMe } from '../../hooks/useMe';
import { useReferral } from '../../hooks/useReferral';
import { patchMe, startEmail, verifyEmail, setBiometricApi, removePinApi } from '../../lib/me';
import { clearWebSession, hasApiAuth, isWebSession } from '../../lib/webSession';
import { setWebUserId } from '../../lib/sessionUser';
import { runBiometric, biometricSupported } from '../../lib/biometric';
import { persistLanguage } from '../../i18n';
import keyIcon from '../../assets/figma/profile/key.svg';
import shieldIcon from '../../assets/figma/profile/shield.svg';
import documentIcon from '../../assets/figma/profile/document.svg';
import bellIcon from '../../assets/figma/profile/bell.svg';
import messageIcon from '../../assets/figma/profile/message.svg';
import refreshIcon from '../../assets/figma/profile/refresh.svg';
import addressesIcon from '../../assets/figma/profile/addresses.svg';
import logoutIcon from '../../assets/figma/profile/logout.svg';
import { useQueryClient } from '@tanstack/react-query';
import { useUiStore } from '../../store/ui';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

function Row({
  icon,
  label,
  subtitle,
  tint,
  right,
  soon,
  soonLabel,
  onClick,
}: {
  icon: string;
  label: string;
  subtitle?: string;
  tint: string;
  right?: ReactNode;
  soon?: boolean;
  soonLabel?: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className="flex w-full items-center justify-between gap-2 py-1.5 text-left active:opacity-70 disabled:active:opacity-100"
    >
      <span className="flex min-w-0 items-center gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px]"
          style={{ backgroundColor: tint }}
        >
          <img src={icon} alt="" className="h-5 w-5" />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="text-[14px] tracking-[-0.05px] text-ink">{label}</span>
          {subtitle && <span className="text-[12px] text-muted">{subtitle}</span>}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2 text-[13px] text-muted">
        {soon && soonLabel && (
          <span className="rounded-[9px] bg-negative/10 px-2 py-0.5 text-[11px] font-bold text-negative">
            {soonLabel}
          </span>
        )}
        {right}
        {onClick && <ChevronRight size={16} className="text-muted" />}
      </span>
    </button>
  );
}

function Group({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2 rounded-[24px] bg-white p-3">{children}</div>;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-1 text-[11px] font-bold uppercase tracking-[0.5px] text-muted">{children}</p>
  );
}

function Divider() {
  return <div className="mx-auto h-px w-[calc(100%-12px)] bg-black/[0.06]" />;
}

function Toggle({
  on,
  disabled,
  onClick,
}: {
  on: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick?.();
      }}
      className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors ${
        on ? 'bg-[#34c759]' : 'bg-black/15'
      } ${disabled ? 'opacity-40' : ''}`}
    >
      <span
        className={`absolute h-5 w-5 rounded-full bg-white shadow transition-transform ${
          on ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </span>
  );
}

export function ProfileScreen() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const tgUser = useTelegramUser();
  const { me, links, faq, initData, refresh, canFetch } = useMe();
  const { rewardPct, enabled: referralEnabled, vaults: referralVaults } = useReferral();
  const referralVaultsLabel =
    referralVaults.length === 0
      ? t('referral.vaultsNone')
      : referralVaults
          .map((k) =>
            k === 'liquid'
              ? t('referral.vaultLiquid')
              : k === 'monthly'
                ? t('referral.vaultMonthly')
                : k === 'maximum'
                  ? t('referral.vaultMaximum')
                  : k,
          )
          .join(i18n.language.startsWith('ru') || i18n.language.startsWith('uk') ? ' и ' : ' & ');
  const name =
    telegramName(tgUser, '') ||
    me?.nickname ||
    me?.email ||
    t('wallet.username');
  const avatarUrl = tgUser?.photo_url || me?.avatarUrl || null;
  const webSession = isWebSession();


  const [langOpen, setLangOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [pinMode, setPinMode] = useState<'set' | 'change' | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [amlOpen, setAmlOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [reauthAction, setReauthAction] = useState<'removePin' | 'disableBio' | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const current = resolveLocale(i18n.language);
  const currentLang = LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0]!;
  const [bioSupported, setBioSupported] = useState(false);
  useEffect(() => {
    void biometricSupported().then(setBioSupported);
  }, []);

  // BottomNav is portaled to document.body above #root, so full-screen profile
  // overlays must hide it (same counter BottomSheet uses) or they sit under the nav.
  const openSheet = useUiStore((s) => s.openSheet);
  const closeSheet = useUiStore((s) => s.closeSheet);
  const coverNav = !!(
    pinMode ||
    notifOpen ||
    seedOpen ||
    faqOpen ||
    promoOpen ||
    amlOpen ||
    refOpen ||
    supportOpen ||
    reauthAction
  );
  useEffect(() => {
    if (!coverNav) return;
    openSheet();
    return () => closeSheet();
  }, [coverNav, openSheet, closeSheet]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  };

  const changeLanguage = (code: string) => {
    const lang = resolveLocale(code);
    void i18n.changeLanguage(lang);
    persistLanguage(lang);
    setLangOpen(false);
    if (canFetch)
      patchMe(initData, { language: lang })
        .then(refresh)
        .catch(() => {});
  };

  const copyTelegramId = () => {
    if (!tgUser?.id) return;
    navigator.clipboard?.writeText(String(tgUser.id)).then(
      () => showToast(t('profile.idCopied')),
      () => {},
    );
  };

  const togglePin = () => {
    if (!me) return;
    if (!me.pinSet) {
      setPinMode('set');
      return;
    }
    // Web money/seed-key require a server PIN — do not allow removal here.
    if (webSession) {
      showToast(t('profile.pinRequiredWeb'));
      return;
    }
    // Turning PIN off — blocked while biometric is on.
    if (me.biometricEnabled) {
      showToast(t('profile.disableBiometricFirst'));
      return;
    }
    // Require the current PIN before removing it.
    setReauthAction('removePin');
  };

  const toggleBiometric = async () => {
    if (!me || !canFetch) return;
    // Disabling requires biometric reauth.
    if (me.biometricEnabled) {
      setReauthAction('disableBio');
      return;
    }
    // Enabling needs hardware support — tell the user instead of doing nothing.
    if (!bioSupported) {
      showToast(t('profile.biometricUnsupported'));
      return;
    }
    if (!me.pinSet) {
      showToast(t('profile.pinRequiredFirst'));
      setPinMode('set');
      return;
    }
    const ok = await runBiometric(t('profile.biometricReason'));
    if (!ok) return;
    await setBiometricApi(initData, true).catch(() => {});
    refresh();
    showToast(t('profile.biometricOn'));
  };

  const username = tgUser?.username;

  return (
    <div className="relative h-full min-h-full">
      <ScreenBackdrop full inTree={false} />

      <div className="nav-pad relative z-10 flex flex-col px-4 pt-[calc(var(--tg-safe-top)_+_3rem)]">
        {/* Head */}
        <div className="flex flex-col items-center gap-3">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-[100px] w-[100px] rounded-full object-cover shadow-sm"
            />
          ) : (
            <div className="flex h-[100px] w-[100px] items-center justify-center rounded-full bg-white text-[36px] font-medium text-ink shadow-sm">
              {name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex flex-col items-center gap-1">
            <span className="text-[20px] font-medium text-ink">{name}</span>
            {username && <span className="text-[14px] text-[#377dff]">@{username}</span>}
            {!username && me?.email && (
              <span className="text-[14px] text-muted">{me.email}</span>
            )}
            {tgUser?.id && (
              <span className="text-[13px] text-muted">
                {t('profile.telegramId')}: {tgUser.id}
              </span>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4">
          {/* Account */}
          <Group>
            {tgUser?.id && (
              <>
                <Row
                  icon={documentIcon}
                  tint="#eef2ff"
                  label={t('profile.telegramId')}
                  right={<span className="text-[#377dff]">{tgUser.id}</span>}
                  onClick={copyTelegramId}
                />
                <Divider />
              </>
            )}
            <Row
              icon={messageIcon}
              tint="#e9f6ef"
              label={t('profile.email')}
              right={
                me?.email ? (
                  <span className="flex items-center gap-1.5">
                    <span>{me.email.length > 26 ? `${me.email.slice(0, 26)}…` : me.email}</span>
                    {me.emailVerified ? (
                      <span className="text-positive">✓</span>
                    ) : (
                      <span className="text-[#ff9500]">!</span>
                    )}
                  </span>
                ) : (
                  <span className="text-[#377dff]">{t('profile.add')}</span>
                )
              }
              onClick={() => setEmailOpen(true)}
            />
          </Group>

          {webSession && (
            <Group>
              <Row
                icon={logoutIcon}
                tint="#fde8e8"
                label={t('profile.signOut')}
                onClick={() => {
                  clearWebSession();
                  setWebUserId(null);
                  qc.clear();
                  window.location.reload();
                }}
              />
            </Group>
          )}

          {/* Security */}
          <Group>
            <Row
              icon={keyIcon}
              tint="#eee6ff"
              label={t('profile.pin')}
              right={
                <span className="flex items-center gap-3">
                  {me?.pinSet && (
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPinMode('change');
                      }}
                      className="text-[13px] text-[#377dff]"
                    >
                      {t('profile.change')}
                    </span>
                  )}
                  <Toggle on={!!me?.pinSet} onClick={togglePin} />
                </span>
              }
              onClick={togglePin}
            />
            <Divider />
            <Row
              icon={shieldIcon}
              tint="#e9f6ef"
              label={t('profile.biometric')}
              right={<Toggle on={!!me?.biometricEnabled} onClick={toggleBiometric} />}
              onClick={toggleBiometric}
            />
            <Divider />
            <Row
              icon={keyIcon}
              tint="#eee6ff"
              label={t('profile.seedPhrase')}
              onClick={() => setSeedOpen(true)}
            />
          </Group>

          {/* Language */}
          <div className="relative rounded-[24px] bg-white p-3">
            <button
              onClick={() => setLangOpen((o) => !o)}
              aria-expanded={langOpen}
              className="flex w-full items-center justify-between py-1.5 text-left active:opacity-70"
            >
              <span className="flex items-center gap-3">
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-[12px] text-[18px] leading-none"
                  style={{ backgroundColor: '#eef2ff' }}
                >
                  {currentLang.flag}
                </span>
                <span className="text-[14px] tracking-[-0.05px] text-ink">
                  {t('profile.language')}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-[13px] text-muted">{currentLang.label}</span>
                <ChevronRight
                  size={16}
                  className={`text-muted transition-transform duration-300 ${langOpen ? 'rotate-90' : ''}`}
                />
              </span>
            </button>

            {langOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setLangOpen(false)} />
                <div
                  className="absolute inset-x-3 top-[calc(100%-4px)] z-30 max-h-[min(50vh,320px)] overflow-y-auto overscroll-contain rounded-[16px] bg-white shadow-[0px_12px_40px_rgba(0,0,0,0.16)]"
                  onClick={(e) => e.stopPropagation()}
                >
                  {LANGUAGES.map((l, i) => (
                    <button
                      key={l.code}
                      onClick={() => changeLanguage(l.code)}
                      className={`flex w-full items-center gap-2.5 px-4 py-3 text-left active:bg-black/[0.03] ${
                        i > 0 ? 'border-t border-black/[0.06]' : ''
                      }`}
                    >
                      <span className="text-[18px] leading-none">{l.flag}</span>
                      <span className="flex-1 text-[14px] font-medium text-ink">{l.label}</span>
                      {l.code === current && <span className="text-[#377dff]">✓</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Finance — roadmap rows (скоро) */}
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('profile.sectionFinance')}</SectionLabel>
            <Group>
              <Row
                icon={documentIcon}
                tint="#f0f0f2"
                label={t('profile.accountDetails')}
                subtitle={t('profile.accountDetailsDesc')}
                soon
                soonLabel={t('comingSoon.badge')}
                onClick={() => showToast(t('comingSoon.subtitle'))}
              />
              <Divider />
              <Row
                icon={shieldIcon}
                tint="#f0f0f2"
                label={t('profile.amlCheck')}
                subtitle={t('profile.amlCheckDesc')}
                onClick={() => setAmlOpen(true)}
              />
              <Divider />
              <Row
                icon={refreshIcon}
                tint="#f0f0f2"
                label={t('profile.expenseAnalytics')}
                subtitle={t('profile.expenseAnalyticsDesc')}
                soon
                soonLabel={t('comingSoon.badge')}
                onClick={() => showToast(t('comingSoon.subtitle'))}
              />
              <Divider />
              <Row
                icon={documentIcon}
                tint="#f0f0f2"
                label={t('profile.statement')}
                subtitle={t('profile.statementDesc')}
                soon
                soonLabel={t('comingSoon.badge')}
                onClick={() => showToast(t('comingSoon.subtitle'))}
              />
            </Group>
          </div>

          {/* Services — roadmap (скоро) */}
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('profile.sectionServices')}</SectionLabel>
            <Group>
              <Row
                icon={addressesIcon}
                tint="#f0f0f2"
                label={t('profile.referralSystem')}
                subtitle={
                  referralEnabled
                    ? t('profile.referralSystemDesc', {
                        pct: rewardPct ?? '—',
                        vaults: referralVaultsLabel,
                      })
                    : t('profile.referralSystemPaused')
                }
                onClick={() => setRefOpen(true)}
              />
              <Divider />
              <Row
                icon={bellIcon}
                tint="#f0f0f2"
                label={t('profile.esim')}
                subtitle={t('profile.esimDesc')}
                soon
                soonLabel={t('comingSoon.badge')}
                onClick={() => showToast(t('comingSoon.subtitle'))}
              />
              <Divider />
              <Row
                icon={keyIcon}
                tint="#f0f0f2"
                label={t('profile.businessLounge')}
                subtitle={t('profile.businessLoungeDesc')}
                soon
                soonLabel={t('comingSoon.badge')}
                onClick={() => showToast(t('comingSoon.subtitle'))}
              />
              <Divider />
              <Row
                icon={addressesIcon}
                tint="#f0f0f2"
                label={t('profile.fastLine')}
                subtitle={t('profile.fastLineDesc')}
                soon
                soonLabel={t('comingSoon.badge')}
                onClick={() => showToast(t('comingSoon.subtitle'))}
              />
              <Divider />
              <Row
                icon={documentIcon}
                tint="#f0f0f2"
                label={t('profile.rwa')}
                subtitle={t('profile.rwaDesc')}
                soon
                soonLabel={t('comingSoon.badge')}
                onClick={() => showToast(t('comingSoon.subtitle'))}
              />
            </Group>
          </div>

          {/* More */}
          <Group>
            <Row
              icon={refreshIcon}
              tint="#fff1d6"
              label={t('profile.promocode')}
              onClick={() => setPromoOpen(true)}
            />
            <Divider />
            <Row
              icon={bellIcon}
              tint="#e9f6ef"
              label={t('profile.notifications')}
              onClick={() => setNotifOpen(true)}
            />
            <Divider />
            <Row
              icon={messageIcon}
              tint="#ddeeff"
              label={t('profile.support')}
              onClick={() => setSupportOpen(true)}
            />
            <Divider />
            <Row
              icon={documentIcon}
              tint="#eee6ff"
              label={t('profile.legal')}
              onClick={() =>
                links && window.Telegram?.WebApp?.openLink(links.legal, { try_instant_view: true })
              }
            />
            <Divider />
            <Row
              icon={documentIcon}
              tint="#ddeeff"
              label={t('profile.faq')}
              onClick={() => setFaqOpen(true)}
            />
          </Group>

          <p className="pt-2 text-center text-[12px] text-muted/70">
            Version {formatAppVersion(import.meta.env.VITE_APP_VERSION)}
          </p>
          <p className="pt-1 text-center">
            <button
              type="button"
              onClick={() => window.Telegram?.WebApp?.openLink('https://incpt.io/')}
              className="text-[12px] text-muted underline"
            >
              incpt.io
            </button>
          </p>
        </div>
      </div>

      {/* Edit sheets */}
      <EmailSheet
        open={emailOpen}
        initData={initData}
        onClose={() => setEmailOpen(false)}
        onVerified={() => {
          setEmailOpen(false);
          refresh();
          showToast(t('profile.emailVerified'));
        }}
      />

      {/* PIN flow overlay */}
      {pinMode && canFetch && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-holo">
          <PinFlow
            mode={pinMode}
            initData={initData}
            onClose={() => setPinMode(null)}
            onDone={() => {
              setPinMode(null);
              refresh();
              showToast(t('profile.saved'));
            }}
          />
        </div>
      )}

      {/* Notifications overlay */}
      {notifOpen && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-surface">
          <NotificationsScreen onClose={() => setNotifOpen(false)} />
        </div>
      )}

      {/* Referral overlay — share a link, see who joined */}
      {refOpen && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-surface">
          <ReferralScreen onBack={() => setRefOpen(false)} />
        </div>
      )}

      {/* Promocode overlay — paste a code, get INCPT */}
      {promoOpen && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-holo">
          <PromocodeScreen onBack={() => setPromoOpen(false)} />
        </div>
      )}

      {amlOpen && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-holo">
          <AmlScreen onBack={() => setAmlOpen(false)} />
        </div>
      )}

      {/* FAQ overlay (admin-managed accordion) */}
      {faqOpen && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-surface">
          <FaqScreen items={faq} onClose={() => setFaqOpen(false)} />
        </div>
      )}

      {/* Support overlay (email + Telegram agent, admin-configured) */}
      {supportOpen && links && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-surface">
          <SupportScreen
            email={links.supportEmail}
            telegram={links.supportTelegram}
            onClose={() => setSupportOpen(false)}
          />
        </div>
      )}

      {/* Seed phrase overlay (reauth → warning → reveal) */}
      {seedOpen && canFetch && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-surface">
          <SeedPhraseFlow
            pinSet={!!me?.pinSet}
            biometricEnabled={!!me?.biometricEnabled}
            emailVerified={!!me?.emailVerified}
            email={me?.email ?? null}
            initData={initData}
            onClose={() => setSeedOpen(false)}
          />
        </div>
      )}

      {/* Reauth before removing PIN / disabling biometric */}
      {reauthAction && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={async (token) => {
              const action = reauthAction;
              setReauthAction(null);
              if (action === 'removePin') {
                // Only report success when the server actually cleared the PIN
                // (it re-verifies the reauth token). Never claim it was removed
                // when the call failed — the toggle must follow server truth.
                try {
                  const res = await removePinApi(initData, token);
                  refresh();
                  showToast(res.pinSet ? t('profile.pinSaveFailed') : t('profile.pinRemoved'));
                } catch {
                  refresh();
                  showToast(t('profile.pinSaveFailed'));
                }
              } else {
                await setBiometricApi(initData, false, token).catch(() => {});
                refresh();
              }
            }}
            onCancel={() => setReauthAction(null)}
          />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1rem)]">
          <div className="animate-toast rounded-[18px] bg-ink px-5 py-3 text-[14px] font-medium text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

/** YYYYMMDD-HHmm deploy stamp → YYYY.MM.DD for the profile footer. */
function formatAppVersion(stamp: string | undefined): string {
  const raw = stamp ?? 'dev';
  const m = /^(\d{4})(\d{2})(\d{2})/.exec(raw);
  return m ? `${m[1]}.${m[2]}.${m[3]}` : raw;
}

function EmailSheet({
  open,
  initData,
  onClose,
  onVerified,
}: {
  open: boolean;
  initData: string;
  onClose: () => void;
  onVerified: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'input' | 'code'>('input');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const opened = useRef(false);
  useEffect(() => {
    if (open && !opened.current) {
      opened.current = true;
      setStep('input');
      setEmail('');
      setCode('');
      setError(null);
    }
    if (!open) opened.current = false;
  }, [open]);

  const send = async () => {
    if (!hasApiAuth(initData) || !/^\S+@\S+\.\S+$/.test(email)) {
      setError(t('profile.emailInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await startEmail(initData, email.trim());
      if (!r.sent) setError(t('profile.emailNotSent'));
      setStep('code');
    } catch {
      setError(t('profile.emailInvalid'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!hasApiAuth(initData) || !/^\d{6}$/.test(code)) {
      setError(t('profile.codeInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyEmail(initData, code);
      onVerified();
    } catch {
      setError(t('profile.codeWrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={t('profile.email')}>
      <div className="mt-4 space-y-4">
        {step === 'input' ? (
          <>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              inputMode="email"
              placeholder="you@example.com"
              className="w-full rounded-[16px] bg-[#f5f6f8] px-4 py-3.5 text-[15px] text-ink outline-none"
            />
            {error && <p className="text-[13px] text-[#ff3b30]">{error}</p>}
            <button
              onClick={send}
              disabled={busy}
              className="flex w-full items-center justify-center rounded-full bg-ink py-3.5 text-[15px] font-medium text-white disabled:opacity-40"
            >
              {busy ? '…' : t('profile.sendCode')}
            </button>
          </>
        ) : (
          <>
            <p className="text-[13px] text-muted">
              {t('profile.codeSentTo')} <span className="font-medium text-ink">{email}</span>
            </p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="123456"
              className="w-full rounded-[16px] bg-[#f5f6f8] px-4 py-3.5 text-center text-[20px] tracking-[8px] text-ink outline-none"
            />
            {error && <p className="text-[13px] text-[#ff3b30]">{error}</p>}
            <button
              onClick={verify}
              disabled={busy || code.length !== 6}
              className="flex w-full items-center justify-center rounded-full bg-ink py-3.5 text-[15px] font-medium text-white disabled:opacity-40"
            >
              {busy ? '…' : t('profile.verify')}
            </button>
            <button
              onClick={() => setStep('input')}
              className="w-full text-center text-[13px] text-muted"
            >
              {t('profile.changeEmail')}
            </button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
